import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Send, Loader2, CheckCircle, Building2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import Header from "../components/Header";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CreditApplicationPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    monthly_income: "",
    employment_type: "",
    down_payment: "",
    loan_term_years: "25"
  });

  useEffect(() => {
    fetchListing();
  }, [id]);

  const fetchListing = async () => {
    try {
      const response = await axios.get(`${API}/listings/${id}`);
      setListing(response.data);
      setFormData(prev => ({
        ...prev,
        down_payment: Math.round(response.data.price * 0.15).toString()
      }));
    } catch (error) {
      console.error("Error fetching listing:", error);
      toast.error("Eroare la încărcarea proprietății");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.phone || !formData.email || !formData.monthly_income || !formData.employment_type) {
      toast.error("Completează toate câmpurile obligatorii");
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/credit/apply`, {
        listing_id: id,
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        monthly_income: parseFloat(formData.monthly_income),
        employment_type: formData.employment_type,
        down_payment: parseFloat(formData.down_payment),
        loan_term_years: parseInt(formData.loan_term_years)
      });
      
      setSubmitted(true);
      toast.success("Cerere trimisă cu succes!");
    } catch (error) {
      console.error("Error submitting:", error);
      toast.error("Eroare la trimiterea cererii");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-100">
        <Header />
        <div className="max-w-2xl mx-auto px-4 py-8">
          <div className="animate-pulse">
            <div className="h-8 bg-stone-200 rounded w-1/2 mb-4" />
            <div className="h-64 bg-stone-200 rounded-2xl" />
          </div>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-stone-100">
        <Header />
        <main className="max-w-2xl mx-auto px-4 py-16">
          <div className="bg-white rounded-2xl p-8 card-shadow text-center">
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-emerald-700" />
            </div>
            <h1 className="font-['Outfit'] text-2xl font-bold text-stone-900 mb-2">
              Cerere trimisă cu succes!
            </h1>
            <p className="text-stone-500 mb-8">
              Un consultant de credite te va contacta în cel mai scurt timp pentru a discuta despre opțiunile tale de finanțare.
            </p>
            <div className="flex gap-3 justify-center">
              <Link to={`/proprietate/${id}`}>
                <Button variant="outline" className="rounded-full">
                  Înapoi la proprietate
                </Button>
              </Link>
              <Link to="/">
                <Button className="bg-emerald-950 hover:bg-emerald-900 rounded-full">
                  Vezi alte proprietăți
                </Button>
              </Link>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-100">
      <Header />
      
      <main className="max-w-2xl mx-auto px-4 py-8">
        <Link to={`/proprietate/${id}`} className="text-stone-500 hover:text-stone-700 flex items-center gap-1 mb-6">
          <ArrowLeft className="w-4 h-4" />
          Înapoi la proprietate
        </Link>

        {/* Property Summary */}
        {listing && (
          <div className="bg-emerald-950 rounded-2xl p-6 mb-6 text-white">
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 bg-emerald-900 rounded-xl flex items-center justify-center flex-shrink-0">
                <Building2 className="w-8 h-8" />
              </div>
              <div>
                <h2 className="font-['Outfit'] font-semibold text-lg">{listing.title}</h2>
                <p className="text-emerald-200">{listing.zone}, {listing.city}</p>
                <p className="text-2xl font-bold mt-2">
                  {listing.price.toLocaleString('ro-RO')} €
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Application Form */}
        <div className="bg-white rounded-2xl p-6 sm:p-8 card-shadow">
          <h1 className="font-['Outfit'] text-2xl font-bold text-stone-900 mb-2">
            Aplică pentru credit ipotecar
          </h1>
          <p className="text-stone-500 mb-6">
            Completează datele tale și un consultant te va contacta pentru a discuta opțiunile de finanțare.
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Personal Info */}
            <div className="space-y-4">
              <h3 className="font-semibold text-stone-900">Date personale</h3>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Nume complet *</Label>
                  <Input
                    id="name"
                    data-testid="credit-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="mt-2"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Telefon *</Label>
                  <Input
                    id="phone"
                    data-testid="credit-phone-input"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="mt-2"
                    required
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  data-testid="credit-email-input"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="mt-2"
                  required
                />
              </div>
            </div>

            {/* Financial Info */}
            <div className="space-y-4">
              <h3 className="font-semibold text-stone-900">Date financiare</h3>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="income">Venit lunar net (EUR) *</Label>
                  <Input
                    id="income"
                    data-testid="credit-income-input"
                    type="number"
                    value={formData.monthly_income}
                    onChange={(e) => setFormData({ ...formData, monthly_income: e.target.value })}
                    className="mt-2"
                    placeholder="ex: 1500"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="employment">Tip angajare *</Label>
                  <Select
                    value={formData.employment_type}
                    onValueChange={(v) => setFormData({ ...formData, employment_type: v })}
                  >
                    <SelectTrigger data-testid="credit-employment-select" className="mt-2">
                      <SelectValue placeholder="Selectează" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="angajat">Angajat</SelectItem>
                      <SelectItem value="pfa">PFA / Liber profesionist</SelectItem>
                      <SelectItem value="srl">Administrator SRL</SelectItem>
                      <SelectItem value="pensionar">Pensionar</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="downpayment">Avans disponibil (EUR)</Label>
                  <Input
                    id="downpayment"
                    data-testid="credit-downpayment-input"
                    type="number"
                    value={formData.down_payment}
                    onChange={(e) => setFormData({ ...formData, down_payment: e.target.value })}
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="term">Perioadă credit (ani)</Label>
                  <Select
                    value={formData.loan_term_years}
                    onValueChange={(v) => setFormData({ ...formData, loan_term_years: v })}
                  >
                    <SelectTrigger className="mt-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10 ani</SelectItem>
                      <SelectItem value="15">15 ani</SelectItem>
                      <SelectItem value="20">20 ani</SelectItem>
                      <SelectItem value="25">25 ani</SelectItem>
                      <SelectItem value="30">30 ani</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-stone-200">
              <p className="text-stone-400 text-sm mb-4">
                * Câmpuri obligatorii. Datele tale sunt procesate în conformitate cu GDPR.
              </p>
              
              <Button
                type="submit"
                data-testid="submit-credit-application"
                disabled={submitting}
                className="w-full h-12 bg-amber-500 hover:bg-amber-600 rounded-xl font-semibold"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Se trimite...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Trimite cererea
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default CreditApplicationPage;
